import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { School, Users, BookOpen, Loader2, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";

const JoinClassroom = () => {
  const { joinCode } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [classroom, setClassroom] = useState(null);
  const [error, setError] = useState(null);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    fetchClassroomInfo();
  }, [joinCode]);

  const fetchClassroomInfo = async () => {
    try {
      const response = await axios.get(`${API}/classrooms/invite/${joinCode}`);
      setClassroom(response.data);
    } catch (err) {
      setError("This classroom invite link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!user) {
      // Redirect to register with return URL
      navigate(`/register?redirect=/join/${joinCode}`);
      return;
    }

    if (user.role === "teacher") {
      toast.error("Teachers cannot join classrooms as students");
      return;
    }

    setJoining(true);
    try {
      await axios.post(`${API}/classrooms/join`, 
        { join_code: joinCode },
        { headers: getAuthHeaders(token) }
      );
      setJoined(true);
      toast.success("Successfully joined the classroom!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join classroom");
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-hero-gradient flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4F46E5]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-hero-gradient flex items-center justify-center p-4">
        <Card className="card-base max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <School className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid Invite Link</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Link to="/">
              <Button className="btn-primary">Go to Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (joined) {
    return (
      <div className="min-h-screen bg-hero-gradient flex items-center justify-center p-4">
        <Card className="card-base max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Welcome to {classroom.name}!</h2>
            <p className="text-slate-600 mb-6">
              You've successfully joined the classroom. You can now access shared tests and track your progress.
            </p>
            <Button onClick={() => navigate("/classrooms")} className="btn-primary gap-2">
              Go to My Classrooms
              <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Header */}
      <nav className="container-main py-6">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-br from-[#4F46E5] to-[#14B8A6] rounded-xl flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
          <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            BookTest<span className="text-[#4F46E5]">.pro</span>
          </span>
        </Link>
      </nav>

      {/* Main Content */}
      <div className="container-main py-12 flex items-center justify-center">
        <Card className="card-base max-w-lg w-full">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-[#4F46E5] to-[#14B8A6] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <School className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                You're Invited!
              </h1>
              <p className="text-slate-600">
                You've been invited to join a classroom on BookTest.pro
              </p>
            </div>

            {/* Classroom Info */}
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-1">{classroom.name}</h3>
              {classroom.description && (
                <p className="text-slate-600 text-sm mb-3">{classroom.description}</p>
              )}
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span>Teacher: <strong className="text-slate-700">{classroom.teacher_name}</strong></span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {classroom.student_count} students
                </span>
              </div>
            </div>

            {/* Join Button or Login Prompt */}
            {user ? (
              <Button 
                onClick={handleJoin} 
                disabled={joining || user.role === "teacher"}
                className="w-full btn-primary h-12 gap-2"
              >
                {joining ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Join Classroom
                  </>
                )}
              </Button>
            ) : (
              <div className="space-y-3">
                <Button onClick={handleJoin} className="w-full btn-primary h-12 gap-2">
                  <ArrowRight className="w-5 h-5" />
                  Sign Up to Join
                </Button>
                <p className="text-center text-sm text-slate-500">
                  Already have an account?{" "}
                  <Link to={`/login?redirect=/join/${joinCode}`} className="text-[#4F46E5] hover:underline">
                    Sign in
                  </Link>
                </p>
              </div>
            )}

            {user?.role === "teacher" && (
              <p className="text-center text-sm text-orange-600 mt-4">
                Teachers cannot join classrooms as students. Switch to a student account to join.
              </p>
            )}

            {/* Benefits */}
            <div className="mt-8 pt-6 border-t">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">As a classroom member, you can:</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Access tests shared by your teacher
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Track your scores and progress
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Compete on the class leaderboard
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default JoinClassroom;
